import OPE from 'js/on-page-editing';

import ContentArea from 'components/content-area/ContentArea';
import ContentContainer from 'components/content-container/ContentContainer';
import StickyMenuOnTabs from 'components/sticky-menu-on-tabs/StickyMenuOnTabs';
import { ContentWithSidebar, Content, Sidebar } from 'components/content-with-sidebar';
import Link from 'components/link/Link';
import RichText from 'components/rich-text/RichText';
import OptionsModal from 'components/options-modal/OptionsModal';
import DateCardMedia from 'components/date-card/DateCardMedia';
import EventImage from 'components/event-image/EventImage';
import Label from 'components/label/Label';
import DatesMore from 'components/dates-more/DatesMore';

import EventData from './EventData';
import EventPageLink from './EventPageLink';

import { IEventPage } from 'source/NFR.types';

interface EventPageProps extends IEventPage {
  fetchPriority?: 'auto' | 'high' | 'low';
  loading?: 'eager' | 'lazy';
}

const EventPage: React.FC<EventPageProps> = ({
  contactInfo,
  metadata,
  labels,
  links = [],
  onPageEditing,
  registrationLink,
  eventImage,
  richText,
  schedule,
  speakers,
  title,
  share,
  date,
  media,
  menu,
  moreDates,
  eventTag
}) => {
  return (
    <div className="event-page">
      <ContentContainer theme="wide" className="event-page__header">
        <div className="event-page__image-container">
          <EventImage loading="eager" fetchPriority="high" className="event-page--event-image" {...eventImage} />
        </div>
        <div className="event-page__header-text">
          {eventTag && <div className="event-page__header-tag">{eventTag}</div>}
          {title && (
            <h1 data-epi-type="title" className="event-page__title" {...OPE(onPageEditing?.title)}>
              {title}
            </h1>
          )}
          <div className="event-page__header-content" data-epi-type="content">
            <EventData {...metadata} />
            <div className="event-page__date-container">
              {date && (
                <time className="event-page__date-container-date" dateTime={date}>
                  {date}
                </time>
              )}
              {moreDates && (
                <time className="event-page__date-container-dates-more-container" dateTime={date}>
                  <DatesMore className="event-page__date-container-dates-more" {...moreDates} />
                </time>
              )}
            </div>
            {media?.items?.length > 0 && <DateCardMedia className="event-page__header-media" {...media} />}
            {registrationLink?.url && (
              <div className="event-page__header-registration">
                <Link theme={['primary', 'large']} useButtonStyles {...registrationLink} />
              </div>
            )}
          </div>
        </div>
      </ContentContainer>
      <ContentContainer theme="wide" className="event-page__content-container">
        <ContentWithSidebar className="event-page--content-with-sidebar" useContentContainer={false}>
          {menu?.navGroups?.length > 0 && (
            <Sidebar className="event-page--sidebar">
              <StickyMenuOnTabs {...menu} className="event-page--menu" />
            </Sidebar>
          )}
          <Content className="event-page--content">
            <div className="event-page--links">
              {links?.map(link => (
                <EventPageLink key={link.url} {...link} />
              ))}
              {share && <OptionsModal theme="isProposalOrEvent" {...share} />}
            </div>
            {richText && (
              <div className="event-page--text" data-epi-type="content">
                {labels.about && <Label {...labels.about} />}
                <RichText {...richText} />
              </div>
            )}
            {speakers && (
              <div className="event-page--speakers" data-epi-type="content">
                {labels.speakers && <Label {...labels.speakers} />}
                <ContentArea {...speakers} />
              </div>
            )}
            {schedule && (
              <div className="event-page--schedule" data-epi-type="content">
                {labels.schedule && <Label {...labels.schedule} />}
                <ContentArea className="event-page--schedule-content" {...schedule} />
              </div>
            )}
            {contactInfo && (
              <div className="event-page--contact" id={labels.contact.htmlId}>
                <ContentArea enableElementSizing={false} theme="twoColumns" {...contactInfo} />
              </div>
            )}
          </Content>
        </ContentWithSidebar>
      </ContentContainer>
    </div>
  );
};

EventPage.displayName = 'EventPage';

export default EventPage;
